// Unpublished Work © 2025-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import PartsMultiSelect from 'Ui/components/common/parts-selectors/parts-multi-select';
import {openDialog as openReduxDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import {simpleRoundValue} from 'Common/utils/units/unit-converter';
import {isNullOrUndefined} from 'Common/utils/validation-utils';

function getSubtitle(part, subTitleSelector, additionalSubTitleSelector) {
    const baseSubtitle = part[subTitleSelector];

    return additionalSubTitleSelector ?
        `${baseSubtitle} - ${part[additionalSubTitleSelector]}` :
        baseSubtitle;
}

function PartSelector(props) {
    const {
        additionalSubTitleSelector,
        availableParts,
        fetchBinLocations,
        binLocations = [],
        currencyPreference,
        disabled,
        hideBinLocationInput,
        hideFinancialData,
        inventories = [],
        inventoryParts = [],
        openPartDialog,
        partsInputComponent,
        readOnly,
        selectedParts,
        setAvailableParts,
        setPartRequestsToRemove,
        setSelectedParts,
        setValid,
        showAddNewPartOption,
        subTitleSelector,
        titleSelector,
        translations,
        selectedPartsClassName,
        newPartQuantity = 0
    } = props;

    const filteredParts = React.useMemo(() => {
        return availableParts
            .filter(({partId}) => selectedParts.every(({id: selectedId}) => selectedId !== partId))
            .map((part) => ({
                ...part,
                id: part.partId,
                title: part[titleSelector] || '',
                subtitle: getSubtitle(part, subTitleSelector, additionalSubTitleSelector)
            }));
    }, [availableParts, selectedParts]);

    function onPartSelect([partId]) {
        let selectedPart = filteredParts.find(({id}) => id === partId);
        const partInInventory = inventoryParts.find((iP) => iP.partId === selectedPart.partId);

        if (partInInventory) {
            // Fallback for legacy part_bin rows where lastUnitCost may be null.
            const unitCost = partInInventory.lastUnitCost ?? partInInventory.unitCost;
            const roundedUnitCost = simpleRoundValue(unitCost);
            const binLocation = binLocations.find((bL) => bL.id === partInInventory.binLocationId);

            selectedPart = {
                ...selectedPart,
                ...!isNullOrUndefined(unitCost) && {
                    unitCost: roundedUnitCost
                },
                binLocationId: binLocation?.id || '',
                binLocation: binLocation?.title || ''
            };
        }

        setSelectedParts([...selectedParts, selectedPart]);
    }

    const inventoryId = inventories[0]?.inventoryId;

    const addPartFooterAction = showAddNewPartOption ? {
        disabled: false,
        onClick() {
            openPartDialog({
                inventories,
                onPartSave(savedPart) {
                    const mappedPart = {
                        ...savedPart,
                        id: savedPart.partId,
                        title: savedPart[titleSelector],
                        subtitle: getSubtitle(savedPart, subTitleSelector, additionalSubTitleSelector),
                        quantity: newPartQuantity,
                        unitCost: 0
                    };

                    if (!selectedParts.some(({id}) => id === mappedPart.id)) {
                        setSelectedParts([...selectedParts, mappedPart]);

                        if (setAvailableParts) {
                            setAvailableParts([...availableParts, mappedPart]);
                        }
                    }
                },
                translations
            });
        },
        title: translations.ONLINK_ADD_NEW_PART
    } : null;

    return (
        <PartsMultiSelect
            addPartFooterAction={addPartFooterAction}
            binLocations={binLocations}
            currencyPreference={currencyPreference}
            disabled={disabled}
            fetchBinLocations={fetchBinLocations}
            filteredParts={filteredParts}
            hideBinLocationInput={hideBinLocationInput}
            hideFinancialData={hideFinancialData}
            inventoryId={inventoryId}
            inventoryParts={inventoryParts}
            onPartSelect={onPartSelect}
            partsInputComponent={partsInputComponent}
            readOnly={readOnly}
            selectedParts={selectedParts}
            selectedPartsClassName={selectedPartsClassName}
            setPartRequestsToRemove={setPartRequestsToRemove}
            setSelectedParts={setSelectedParts}
            setValid={setValid}
            translations={translations}
        />
    );
}

PartSelector.propTypes = {
    additionalSubTitleSelector: PropTypes.string,
    availableParts: PropTypes.arrayOf(PropTypes.object),
    binLocations: PropTypes.arrayOf(PropTypes.object),
    currencyPreference: PropTypes.string,
    disabled: PropTypes.bool,
    fetchBinLocations: PropTypes.func,
    hideBinLocationInput: PropTypes.bool,
    hideFinancialData: PropTypes.bool,
    inventories: PropTypes.arrayOf(PropTypes.object),
    inventoryParts: PropTypes.arrayOf(PropTypes.object),
    newPartQuantity: PropTypes.number,
    openPartDialog: PropTypes.func,
    partsInputComponent: PropTypes.elementType,
    readOnly: PropTypes.bool,
    selectedParts: PropTypes.arrayOf(PropTypes.object),
    selectedPartsClassName: PropTypes.string,
    setAvailableParts: PropTypes.func,
    setPartRequestsToRemove: PropTypes.func,
    setSelectedParts: PropTypes.func,
    setValid: PropTypes.func,
    showAddNewPartOption: PropTypes.bool,
    subTitleSelector: PropTypes.string,
    titleSelector: PropTypes.string,
    translations: PropTypes.translations
};

export function mapDispatchToProps(dispatch) {
    return {
        openPartDialog(value) {
            dispatch(openReduxDialog(dialogTypes.PART_DIALOG, value));
        }
    };
}

export default connect(null, mapDispatchToProps)(PartSelector);
